import { useWsMatchStats } from '@/app/core/websocket/hooks';
import { SkeletonMatchStatsComponent } from '@/app/pages/sports/football/match-details/components';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { StatsIndicatorComponent } from '../../../../components/stats-indicator/stats-indicator.component';
import { MatchStatsService } from '../../../../services';

@Component({
  selector: 'app-stats',
  standalone: true,
  imports: [
    StatsIndicatorComponent,
    TranslateModule,
    SkeletonMatchStatsComponent,
  ],
  templateUrl: './stats.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StatsComponent {
  private activeRoute = inject(ActivatedRoute);

  matchStatsService = inject(MatchStatsService);
  matchId = signal<string | null>(null);

  private refreshInterval!: ReturnType<typeof setInterval>;

  baseMatchStats = computed(() => this.matchStatsService.matchStats());
  wsMatchStats = useWsMatchStats();
  wsMatchStatsData = effect(
    () => {
      if (!this.matchId() || !this.wsMatchStats.data()[this.matchId()!]) return;

      this.matchStatsService.setWsMatchStats(
        this.wsMatchStats.data()[this.matchId()!]
      );
    },
    { allowSignalWrites: true }
  );

  readonly liveMatchStats = computed(() => {
    const wsMatchStats = this.matchStatsService.wsMatchStats();

    const newMatchStats = wsMatchStats
      ? {
          ...this.baseMatchStats(),
          Stats: this.baseMatchStats().Stats.map((stat) => {
            const wsMatchStat = wsMatchStats.stats.find(
              (wsStat) => wsStat.kind === stat.Kind
            );

            if (!wsMatchStat) return stat;

            return {
              ...stat,
              HomeValue: wsMatchStat.homeValue,
              AwayValue: wsMatchStat.awayValue,
            };
          }),
        }
      : this.baseMatchStats();

    this.baseMatchStats = signal(newMatchStats);

    return newMatchStats;
  });

  constructor() {
    this.activeRoute.paramMap.subscribe((params) => {
      this.matchId.set(params.get('matchId'));
    });

    if (this.matchId()) {
      this.matchStatsService.getMatchStats(this.matchId()!);

      this.refreshInterval = setInterval(() => {
        this.matchStatsService.getMatchStats(this.matchId()!, true);
      }, 1000 * 60 * 5); // cada 5 minutos
    }
  }

  ngOnDestroy() {
    clearInterval(this.refreshInterval);
    this.wsMatchStats.disconnect();
    this.matchStatsService.wsMatchStats.set(null);
  }
}
